import { useTolgee, useTranslations } from '@aikoa/i18n/react'
import { observer } from '@legendapp/state/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Redirect, useFocusEffect, useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { View } from 'react-native'
import { ContactRequestResponseFlow } from '~/components/contacts/ContactRequestResponseFlow'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import { useAppView } from '~/components/layout/AppViewContext'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import RealtorCard from '~/components/realtors/RealtorCard'
import { Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { PolicyLinks } from '~/components/user/PolicyLinks'
import { useSession } from '~/components/user/SessionProvider'
import { ConfettiTrigger } from '~/lib/ConfettiProvider'
import { trpc } from '~/lib/trpc'

const ContactRequest = observer(function ContactRequest() {
  const { setBackgroundTheme } = useAppView()

  useFocusEffect(
    useCallback(() => {
      setBackgroundTheme(0)
    }, [setBackgroundTheme])
  )

  return (
    <ScreenView>
      <ContactRequestContent />
    </ScreenView>
  )
})

export default ContactRequest

type ResponseStatus = 'pending' | 'accepted' | 'rejected' | 'not-buying' | undefined

const ContactRequestContent = observer(function ContactRequestContent() {
  const { t } = useTranslations(['contactRequest', 'common'])
  const { contactRequestId } = useLocalSearchParams<{
    contactRequestId: string
  }>()

  const [responseStatus, setResponseStatus] = useState<ResponseStatus>(undefined)
  const { showAlert } = useAlert()
  const undoContactRequestResponseMutation = useMutation(
    trpc.contacts.undoContactRequestResponse.mutationOptions()
  )

  const {
    data: requestData,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    trpc.contacts.contactRequestData.queryOptions({
      contactRequestId,
    })
  )

  const { session } = useSession()
  const tolgee = useTolgee(['language'])

  useEffect(() => {
    // Allow LanguageSyncProvider to set the user language if authenticated. Bail if requestData is not loaded yet.
    if (session?.user || !requestData) {
      return
    }

    const userLanguage =
      requestData && 'data' in requestData && requestData?.data?.contact?.language

    if (userLanguage) {
      void tolgee.changeLanguage(userLanguage)
    }
  }, [session, requestData, tolgee])

  useEffect(() => {
    if (requestData && 'data' in requestData && requestData.data.status !== 'pending') {
      setResponseStatus(requestData.data.status)
    }
  }, [requestData])

  if (requestData && 'error' in requestData && requestData.error === 'NOT_FOUND') {
    return <Redirect href="/" />
  }

  const realtorUserId =
    requestData && 'data' in requestData && requestData.data.realtor_user_id
      ? requestData.data.realtor_user_id
      : requestData && 'realtor_user_id' in requestData && requestData.realtor_user_id
        ? requestData.realtor_user_id
        : null

  const isGeneralError = isError || (!isLoading && !requestData)

  const dataError =
    requestData &&
    'error' in requestData &&
    requestData.error &&
    requestData.error !== 'ALREADY_RESPONDED'
      ? requestData.error
      : null

  const requestPayload = requestData && 'data' in requestData ? requestData.data : null
  const isContactInactive = requestPayload?.contact.disabled_reason === 'INACTIVE'

  return (
    <>
      {isLoading && <LoadingView />}

      {(isGeneralError || dataError) && (
        <ContentBox>
          <View className="items-center gap-4">
            <Icon name="alert-circle" size={48} className="text-destructive" />
            <Heading2 className="text-center text-2xl">{t(($) => $.errors.loadError)}</Heading2>
            <SectionContent>{t(($) => $.errors.notFound)}</SectionContent>
          </View>
          {realtorUserId && (
            <View className="mt-8 items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
              <Label className="text-center font-light text-sm">
                {t(($) => $.errors.contactRealtor)}
              </Label>
              <RealtorCard userId={realtorUserId} />
            </View>
          )}
        </ContentBox>
      )}

      {requestData && 'error' in requestData && requestData.error === 'ALREADY_RESPONDED' ? (
        <ContentBox contentContainerClassName="gap-6">
          <Heading2 className="text-center font-bold text-3xl">
            {t(($) => $.alreadyResponded.title)}
          </Heading2>
          <SectionContent>{t(($) => $.alreadyResponded.description)}</SectionContent>
          {realtorUserId && (
            <View className="items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
              <Label className="text-center font-light text-sm">
                {t(($) => $.alreadyResponded.realtorContact)}
              </Label>
              <RealtorCard userId={realtorUserId} showContactInfo />
            </View>
          )}
        </ContentBox>
      ) : responseStatus === 'accepted' ? (
        <ContentBox contentContainerClassName="relative gap-6">
          <ConfettiTrigger
            confettiKey="contact-request-accepted"
            className="size-[64px] items-center self-center">
            <Icon name="checkmark-circle" size={64} className="text-secondary" />
          </ConfettiTrigger>
          <Heading2 className="text-center font-bold text-3xl">
            {t(($) => $.accepted.title)}
          </Heading2>
          <View className="gap-4">
            <SectionContent textClassName="text-pretty text-center text-lg/normal">
              {t(($) => $.accepted.description)}
            </SectionContent>
          </View>
          {realtorUserId && (
            <View className="items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
              <Label className="text-center font-light text-sm">
                {t(($) => $.accepted.realtorContact)}
              </Label>
              <RealtorCard userId={realtorUserId} showContactInfo />
            </View>
          )}
          <LinkButton variant="primary" href="/">
            {t(($) => $.accepted.continue)}
          </LinkButton>
        </ContentBox>
      ) : isContactInactive && requestPayload ? (
        <ContentBox contentContainerClassName="relative gap-6">
          <View className="items-center self-center">
            <Icon name="ban" size={42} className="text-destructive" />
          </View>
          <View className="gap-6">
            <Heading2 className="text-center font-medium text-2xl">
              {t(($) => $.inactive.title)}
            </Heading2>
            <SectionContent>{t(($) => $.inactive.description)}</SectionContent>
            <Button
              variant="primary"
              disabled={undoContactRequestResponseMutation.isPending}
              onPress={() => {
                undoContactRequestResponseMutation.mutate(
                  {
                    contactRequestId,
                  },
                  {
                    onSuccess: (result) => {
                      if (!result.success) {
                        showAlert({
                          type: 'error',
                          title: t(($) => $.errors.title),
                          description: t(($) => $.errors.responseSendFailed),
                        })
                        return
                      }

                      setResponseStatus('pending')
                      void refetch()
                    },
                    onError: () => {
                      showAlert({
                        type: 'error',
                        title: t(($) => $.errors.title),
                        description: t(($) => $.errors.responseSendFailed),
                      })
                    },
                  }
                )
              }}>
              <ButtonLabel>{t(($) => $.inactive.undo)}</ButtonLabel>
            </Button>
          </View>
        </ContentBox>
      ) : requestData && 'data' in requestData ? (
        <ContactRequestResponseFlow
          contactRequest={requestData.data}
          onComplete={async (status) => {
            setResponseStatus(status === 'not-buying' ? 'rejected' : status)
            void refetch()
          }}
        />
      ) : null}
      <PolicyLinks theme="dark" />
    </>
  )
})
